// Money is integer cents everywhere. Nothing in here invents a figure: a null
// stays an em dash rather than becoming a zero.

export const DASH = "—";

/** Postgres bigint/numeric can arrive as a string. Coerce before any maths. */
export type Num = number | string | null | undefined;

function n(v: Num): number | null {
  if (v === null || v === undefined || v === "") return null;
  const x = typeof v === "number" ? v : Number(v);
  return Number.isFinite(x) ? x : null;
}

/** Compact above $10k ("$12.4k"), grouped below it ("$9,840"). */
export function money(value: Num, currency = "USD"): string {
  const cents = n(value);
  if (cents === null) return DASH;
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents) / 100;
  const sym = symbol(currency);
  if (abs >= 1_000_000) return `${sign}${sym}${trim(abs / 1_000_000)}M`;
  if (abs >= 10_000) return `${sign}${sym}${trim(abs / 1_000)}k`;
  return `${sign}${sym}${abs.toLocaleString("en-US", {
    minimumFractionDigits: abs < 100 && abs % 1 !== 0 ? 2 : 0,
    maximumFractionDigits: abs < 100 ? 2 : 0,
  })}`;
}

/** Always two significant-ish digits, never a trailing ".0". */
function trim(n: number): string {
  return n >= 100 ? n.toFixed(0) : n.toFixed(1).replace(/\.0$/, "");
}

function symbol(currency: string): string {
  switch (currency) {
    case "USD":
    case "AUD":
    case "CAD":
    case "NZD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    default:
      return `${currency} `;
  }
}

export function count(value: Num): string {
  const v = n(value);
  if (v === null) return DASH;
  if (Math.abs(v) >= 10_000) return `${trim(v / 1_000)}k`;
  return v.toLocaleString("en-US");
}

export function ratio(value: Num): string {
  const v = n(value);
  return v === null ? DASH : v.toFixed(1);
}

export type Delta = { label: string; tone: Tone };
export type Tone = "pos" | "neg" | "flat";

/**
 * Period-over-period change. `better` says which direction is good — CAC going
 * up is bad, revenue going up is good, spend on its own is neither.
 */
export function delta(
  nowValue: Num,
  prevValue: Num,
  better: "up" | "down" | "neutral" = "up"
): Delta | null {
  const now = n(nowValue);
  const prev = n(prevValue);
  if (now === null || prev === null) return null;
  if (!prev) return now ? { label: "new", tone: "flat" } : null;
  const pct = (now - prev) / Math.abs(prev);
  if (Math.abs(pct) < 0.005) return { label: "flat", tone: "flat" };
  const label = `${pct > 0 ? "+" : "−"}${(Math.abs(pct) * 100).toFixed(1)}%`;
  if (better === "neutral") return { label, tone: "flat" };
  const good = better === "up" ? pct > 0 : pct < 0;
  return { label, tone: good ? "pos" : "neg" };
}

export function toneColor(tone: Tone): string {
  return tone === "pos" ? "var(--pos)" : tone === "neg" ? "var(--neg)" : "var(--text-4)";
}

/** "in 12 days" / "3 days ago" / "today" */
export function relativeDays(date: string | null): string | null {
  if (!date) return null;
  const then = new Date(date + (date.length === 10 ? "T00:00:00" : "")).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.round((then - Date.now()) / 86_400_000);
  if (days === 0) return "today";
  if (days > 0) return `in ${days}d`;
  return `${-days}d ago`;
}

export function shortTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export const CHANNEL_LABELS: Record<string, string> = {
  paid_meta: "Meta Ads",
  paid_google: "Google Ads",
  paid_linkedin: "LinkedIn Ads",
  organic_search: "Organic Search",
  ai_referral: "AI / GEO Referral",
  social_x: "X / Twitter",
  social_meta: "Meta (Instagram & Facebook)",
  social_reddit: "Reddit",
  community: "Community",
  affiliate: "Affiliate",
  email: "Email",
  direct: "Direct",
  unattributed: "Unattributed",
};

export function channelLabel(id: string | null): string {
  if (!id) return "Unattributed";
  return CHANNEL_LABELS[id] ?? id;
}

export function channelColor(id: string): string {
  return `var(--ch-${id}, var(--ch-unattributed))`;
}
